import { OBJECT_LIMITS } from '../state/objects.js';
import { isGridless } from './grid.js';
import { axisPositions } from './cells.js';
import { routeBlocked } from './walls.js';
import { footprintSquares } from '../state/fog.js';
import { spanTest } from '../state/zones.js';
import { heightSquares, squaresFor } from './measure.js';
/** A square's key in a reach: "i,j" of the footprint's top-left square. */
export const cellKey = (c) => `${c.i},${c.j}`;
const EPS = 1e-9;
/**
 * The squares a move reaches: each footprint top-left square it can END in, with the least movement it costs. A step
 * costs what `pathCost` charges (the alternation carried along the path, a diagonal into difficult terrain its base 1.5
 * × the multiplier without moving the alternation), so a square's cost here is what dragging there by the cheapest
 * route would be stamped. The start is in it at 0. Dijkstra over (square, diagonals-so-far odd or even).
 */
export function reachable(r) {
    const best = new Map();
    const seen = new Map();
    const anyIn = (c, test) => {
        for (let j = c.j; j < c.j + r.h; j += 1)
            for (let i = c.i; i < c.i + r.w; i += 1)
                if (test({ i, j }))
                    return true;
        return false;
    };
    // A min-queue kept small: board reaches are a few hundred states, so a sorted insert is plenty.
    const odd0 = (r.diagonals ?? 0) % 2 === 1 ? 1 : 0;
    const queue = [{ c: r.start, odd: odd0, cost: 0 }];
    seen.set(`${cellKey(r.start)}:${odd0}`, 0);
    while (queue.length) {
        const { c, odd, cost } = queue.shift();
        if ((seen.get(`${cellKey(c)}:${odd}`) ?? Infinity) < cost - EPS)
            continue;
        const k = cellKey(c);
        const isStart = c.i === r.start.i && c.j === r.start.j;
        if (isStart || !anyIn(c, r.occupied)) {
            if ((best.get(k) ?? Infinity) > cost)
                best.set(k, cost);
        }
        for (let dj = -1; dj <= 1; dj += 1) {
            for (let di = -1; di <= 1; di += 1) {
                if (!di && !dj)
                    continue;
                const n = { i: c.i + di, j: c.j + dj };
                if (n.i < 0 || n.j < 0 || n.i + r.w > r.columns || n.j + r.h > r.rows)
                    continue;
                if (anyIn(n, r.impassable) || r.blocked(c, n))
                    continue;
                const diag = di !== 0 && dj !== 0;
                const hard = r.multiplier !== null && anyIn(n, r.isDifficult);
                if (hard && r.noDifficult)
                    continue;
                let step;
                let nOdd = odd;
                if (hard)
                    step = (r.diagonal === 'straight' ? (diag ? Math.SQRT2 : 1) : !diag ? 1 : r.diagonal === 'alternate' ? 1.5 : r.diagonal === 'two' ? 2 : 1) * r.multiplier;
                else if (!diag)
                    step = 1;
                else if (r.diagonal === 'straight')
                    step = Math.SQRT2;
                else if (r.diagonal === 'one')
                    step = 1;
                else if (r.diagonal === 'two')
                    step = 2;
                else {
                    step = odd ? 2 : 1;
                    nOdd = odd ? 0 : 1;
                }
                const next = cost + step;
                if (next > r.budget + EPS)
                    continue;
                const sk = `${cellKey(n)}:${nOdd}`;
                if ((seen.get(sk) ?? Infinity) <= next + EPS)
                    continue;
                seen.set(sk, next);
                let at = queue.length;
                while (at > 0 && queue[at - 1].cost > next)
                    at -= 1;
                queue.splice(at, 0, { c: n, odd: nOdd, cost: next });
            }
        }
    }
    return best;
}
// ------------------------------------------------------------------------------------------------ a token's speed
/**
 * A token's speed in the system's unit: flying (it carries the system's fly condition) with a fly speed, that (qa-runs#1161);
 * else what the DM typed (`values.speed`), else its sheet's number of the speed purpose; null when nothing says one.
 */
export function tokenSpeed(values, movement) {
    const fly = flySpeed(values, movement);
    if (fly !== null)
        return fly;
    if (typeof values.speed === 'number' && values.speed > 0)
        return values.speed;
    if (!movement)
        return null;
    const n = values.sheet?.find((s) => s.purpose === movement.speed.purpose)?.value;
    return typeof n === 'number' && n > 0 ? n : null;
}
/** Whether a token is flying: it carries the system's fly condition (any case), set by the DM in Properties. */
export const isFlying = (values, movement) => {
    const c = movement?.fly?.condition.toLowerCase();
    return !!c && (values.conditions ?? []).some((x) => x.trim().toLowerCase() === c);
};
/**
 * A flying token's fly speed (qa-runs#1161): what the DM typed in Properties (`values.fly`), else its sheet's number of
 * the fly purpose (a stat block's is stamped there on link); null when not flying or none.
 */
export function flySpeed(values, movement) {
    if (!movement?.fly || !isFlying(values, movement))
        return null;
    if (typeof values.fly === 'number' && values.fly > 0)
        return values.fly;
    const n = values.sheet?.find((s) => s.purpose === movement.fly.purpose)?.value;
    return typeof n === 'number' && n > 0 ? n : null;
}
/** A number out of a stat block's text field by the system's pattern (its first group), or null. */
function numberFromText(text, pattern) {
    if (!pattern || typeof text !== 'string')
        return null;
    const m = new RegExp(pattern, 'i').exec(text.slice(0, 400));
    const n = m?.[1] !== undefined ? Number(m[1]) : NaN;
    return Number.isFinite(n) && n > 0 && n <= 10_000 ? n : null;
}
/** A stat block's fly speed from its text field by the system's pattern ("fly 60 ft."), or null. */
export const flyFromText = (text, fly) => numberFromText(text, fly?.pattern);
/** A stat block's speed from its text field by the system's pattern ("40 ft., climb 20 ft."), or null. */
export const speedFromText = (text, speed) => numberFromText(text, speed?.pattern);
/**
 * A stat block's speeds by the system's movement rule: the speed from the text field it names, read by its pattern,
 * else the number of its purpose; the fly speed from the fly rule's field. The same reading a link stamps on a token.
 */
export function blockSpeeds(stats, fields, movement) {
    if (!movement)
        return {};
    const text = (field) => (field ? stats.find((x) => x.key === field)?.value : undefined);
    const n = speedFromText(text(movement.speed.field), movement.speed) ?? fields?.[movement.speed.purpose];
    const speed = typeof n === 'number' && n > 0 && n <= 10_000 ? n : undefined;
    const fly = movement.fly?.field ? flyFromText(text(movement.fly.field), movement.fly) : null;
    return { ...(speed !== undefined ? { speed } : {}), ...(fly !== null ? { fly } : {}) };
}
/**
 * A token's values with its stat block's speeds where it holds none itself (qa-runs#1161: a token linked before speeds
 * were stamped), read each time and never stored: the speed when it has neither a typed one nor a sheet's, the fly speed
 * as the sheet number of the fly purpose when it has neither. Nothing from the block: the values as they are.
 */
export function withBlockSpeeds(values, block, movement) {
    if (!block || !movement)
        return values;
    const sheet = values.sheet ?? [];
    const speed = block.speed !== undefined && values.speed === undefined && !sheet.some((s) => s.purpose === movement.speed.purpose) ? block.speed : undefined;
    const fly = block.fly !== undefined && movement.fly && values.fly === undefined && !sheet.some((s) => s.purpose === movement.fly.purpose) ? block.fly : undefined;
    if (speed === undefined && fly === undefined)
        return values;
    return { ...values, ...(speed !== undefined ? { speed } : {}), ...(fly !== undefined ? { sheet: [...sheet, { purpose: movement.fly.purpose, words: ['fly'], value: fly }] } : {}) };
}
/**
 * The squares a creature takes from its stat block, by the system's rule (`distance.space`): its space, else its size's;
 * at least one, however small. Null with no rule, or nothing it can read.
 */
export function creatureSquares(combat, d) {
    const rule = d?.space;
    if (!rule)
        return null;
    const size = combat.refs?.[rule.size];
    const own = combat.fields[rule.field];
    const feet = own !== undefined && own > 0 ? own : size !== undefined ? rule.sizes[size] : undefined;
    if (feet === undefined)
        return null;
    const n = Math.min(OBJECT_LIMITS.size, Math.max(1, Math.ceil(feet / d.perSquare)));
    return { w: n, h: n };
}
/** Whether a token wears heavy armor: the DM's word (`values.heavy`), else its sheet's number of the heavy purpose above 0. */
export function tokenHeavy(values, movement) {
    if (typeof values.heavy === 'boolean')
        return values.heavy;
    if (!movement?.heavy)
        return false;
    return (values.sheet?.find((s) => s.purpose === movement.heavy.purpose)?.value ?? 0) > 0;
}
/** The squares of movement a kind of move gives a token of `speed` (the system's unit). */
export const kindBudget = (kind, speed, heavy, d) => kind.squares !== undefined ? kind.squares : (speed * (heavy && kind.heavyTimes !== undefined ? kind.heavyTimes : kind.times)) / d.perSquare;
/** Whether a token owes movement on its turn (qa-runs#366, a follow): it carries the system's `owed` condition, any case. */
export const owesMove = (values, movement) => {
    const c = movement?.owed?.condition.toLowerCase();
    return !!c && (values.conditions ?? []).some((x) => x.trim().toLowerCase() === c);
};
/** The squares a kind gives this token: `kindBudget`, less what it owes (a kind of fixed reach owes nothing). */
export function tokenBudget(kind, speed, values, movement, d) {
    const full = kindBudget(kind, speed, tokenHeavy(values, movement), d);
    return kind.squares === undefined && owesMove(values, movement) ? Math.max(0, full - movement.owed.less / d.perSquare) : full;
}
/**
 * What a turn's record says of moving (qa-runs#1161 slice B): `moved`, a move of a kind was made this turn (the record's
 * flag, or a Move's leftover still held); `stepped`, an `alone` kind (the 5-foot step) was taken — one of its actions spent.
 */
export function turnMoves(turn, movement) {
    if (!turn || !movement)
        return { moved: false, stepped: false };
    const alone = new Set(movement.kinds.filter((k) => k.alone).flatMap((k) => k.spends));
    return { moved: turn.moved === true || turn.moveLeft !== undefined, stepped: turn.spent.some((s) => alone.has(s.kind)) };
}
/**
 * "Move: 25 of 30 ft" — the drag's and the confirmation's words for a move of `squares` out of a reach of `budget`
 * squares. Past the reach it says so before the drop (qa-runs 1263); such a move is warned, never refused (qa-runs#1161).
 */
export const moveOfLabel = (label, squares, budget, d) => {
    const say = (n) => Math.round(n * d.perSquare * 100) / 100;
    const over = squares > budget ? ` (${say(squares - budget)} ${d.unit} past its limit)` : '';
    return `${label}: ${say(squares)} of ${say(budget)} ${d.unit}${over}`;
};
/** "Move: 20 ft left" (qa-runs#1434): a keyboard step's words for what its reach holds after it; past it, by how much. */
export const moveLeftLabel = (label, left, d) => {
    const say = (n) => Math.round(n * d.perSquare * 100) / 100;
    return left >= 0 ? `${label}: ${say(left)} ${d.unit} left` : `${label}: ${say(-left)} ${d.unit} past its limit`;
};
/**
 * The reach of a token on the board, from what this screen holds. Null on a gridless map (nothing is counted there).
 * A step is blocked when the line between the footprint's centres meets a wall that blocks movement (the server's
 * own test of a route, `routeBlocked`).
 */
export function boardReach(b) {
    if (isGridless(b.grid))
        return null;
    const xs = axisPositions(b.grid, b.extent, 'x');
    const ys = axisPositions(b.grid, b.extent, 'y');
    const fp = footprintSquares(b.grid, b.extent, b.token, b.token.size);
    const w = fp.i1 - fp.i0 + 1;
    const h = fp.j1 - fp.j0 + 1;
    const held = new Set();
    const foe = new Set();
    for (const o of b.others) {
        if (o.id === b.token.id)
            continue;
        const f = footprintSquares(b.grid, b.extent, o, o.size);
        for (let j = f.j0; j <= f.j1; j += 1)
            for (let i = f.i0; i <= f.i1; i += 1) {
                held.add(`${i},${j}`);
                if (!o.passable)
                    foe.add(`${i},${j}`);
            }
    }
    const centre = (c) => ({ x: (xs[c.i] + xs[Math.min(xs.length - 1, c.i + w)]) / 2, y: (ys[c.j] + ys[Math.min(ys.length - 1, c.j + h)]) / 2 });
    const inDifficult = spanTest(b.difficult);
    const cells = reachable({
        start: { i: fp.i0, j: fp.j0 }, w, h, budget: b.budget, diagonal: b.distance.diagonal,
        multiplier: b.distance.difficult?.multiplier ?? null, isDifficult: inDifficult, noDifficult: b.noDifficult, diagonals: b.diagonals,
        blocked: (from, to) => routeBlocked(centre(from), [centre(to)], b.walls),
        impassable: (c) => foe.has(cellKey(c)), occupied: (c) => held.has(cellKey(c)),
        columns: xs.length - 1, rows: ys.length - 1,
    });
    const dz = b.climb ? heightSquares(b.climb, b.distance.diagonal) : 0;
    if (dz) {
        // Height (qa-runs#1160): each square's cost with the climb, as the server stamps the move (`withClimb`); out of reach dropped.
        for (const [k, c] of [...cells]) {
            const total = squaresFor(c, dz, b.distance.diagonal);
            if (total > b.budget + EPS)
                cells.delete(k);
            else
                cells.set(k, total);
        }
    }
    return { cells, w, h, xs, ys };
}
/** Every square a reach covers (each end square's whole footprint), as keys — what the board shades. */
export function reachSquares(r) {
    const out = new Set();
    for (const k of r.cells.keys()) {
        const [i, j] = k.split(',').map(Number);
        for (let y = j; y < j + r.h; y += 1)
            for (let x = i; x < i + r.w; x += 1)
                out.add(`${x},${y}`);
    }
    return out;
}
/** The edge of a set of squares, as board segments: each side of a square whose neighbour is not in the set — the area's red border. */
export function reachEdges(squares, xs, ys) {
    const out = [];
    for (const k of squares) {
        const [i, j] = k.split(',').map(Number);
        const x0 = xs[i];
        const x1 = xs[i + 1];
        const y0 = ys[j];
        const y1 = ys[j + 1];
        if (!squares.has(`${i},${j - 1}`))
            out.push([x0, y0, x1, y0]);
        if (!squares.has(`${i},${j + 1}`))
            out.push([x0, y1, x1, y1]);
        if (!squares.has(`${i - 1},${j}`))
            out.push([x0, y0, x0, y1]);
        if (!squares.has(`${i + 1},${j}`))
            out.push([x1, y0, x1, y1]);
    }
    return out;
}
// ------------------------------------------------------------------------------------------------ the mover's screen
/**
 * What a screen's area is worked out from (qa-runs#1161; design/30 §4): the DM's every difficult zone and every wall;
 * a player's `fog.difficult` and the walls they were sent (their explored map's sight-blocking walls, ends only, read as
 * blocking a move — a preview, corrected by the server). Never anything this screen was not given.
 */
export function areaInputs(state) {
    const difficult = state.zones ? state.zones.filter((z) => z.kind === 'difficult').flatMap((z) => z.spans.map((s) => ({ ...s }))) : (state.fog?.difficult ?? []).map((s) => ({ ...s }));
    const walls = (state.walls ?? []).map((w) => ({ a: { ...w.a }, b: { ...w.b }, move: w.move ?? true, sight: w.sight ?? true, ...(w.door ? { door: { open: w.door.open } } : {}) }));
    return { difficult, walls };
}
/** Whether a mover may pass through another token's squares (qa-runs#1157 sides): a foe's never, a friend's or neutral's yes. */
export const passableFor = (mover, other) => !((mover === 'foe' && other === 'friend') || (mover === 'friend' && other === 'foe'));
export const NO_SPEED_NOTE = 'No speed: set one in Properties.';
/** Why a token moves freely with no area, for the DM (qa-runs#1161): Free move is on, or it is not its turn in the fight. */
export const FREE_MOVE_NOTE = 'Free move is on: no reachable area.';
export const NOT_ITS_TURN_NOTE = 'Not its turn: no reachable area. Moving it is free.';
/** After an `alone` kind (the 5-foot step) the token makes no other move that turn. */
export const stepTakenNote = (label) => `${label} taken: no other move this round.`;
/**
 * The area's plan (qa-runs#1161, Defaults): Free move on, no movement rule or a gridless map — free; in a fight only the
 * token whose turn it is gets an area (the DM moving any other moves it freely); with what is left of a Move, that;
 * a token with no speed moves freely with the note; else the picked kind's reach, Move preselected.
 */
export function movePlan(p) {
    const movement = p.distance?.movement;
    if (!movement || !p.distance || p.gridless)
        return { mode: 'free' };
    if (p.freeMove)
        return { mode: 'free', why: 'freeMove' };
    if (p.fight && !p.fight.acting)
        return { mode: 'free', why: 'notTurn' };
    const speed = tokenSpeed(p.values, movement);
    if (!speed)
        return { mode: 'nospeed' };
    const first = movement.kinds[0];
    // A 5-foot step taken (slice B): no other move this turn — said, and the drop moves freely, checked by the table.
    const step = movement.kinds.find((k) => k.alone);
    if (p.fight?.stepped && step)
        return { mode: 'stepped', kind: step };
    const adders = movement.kinds.filter((k) => k.adds);
    if (p.fight?.moveLeft !== undefined) {
        // What is left of a Run (or another kind not the first, qa-runs#1456): its rest, under its own name, one reach for the turn.
        const of = p.fight.moveOf ? movement.kinds.find((k) => k.key === p.fight.moveOf && k !== first) : undefined;
        if (of)
            return { mode: 'rest', kind: of, budget: p.fight.moveLeft };
        // A Move used up with the turn's other move action still there is a fresh Move, its action spent as any is.
        if (!adders.length && p.fight.moveLeft <= 0 && p.fight.anotherMove)
            return { mode: 'kind', kinds: [first], kind: first, budget: tokenBudget(first, speed, p.values, movement, p.distance) };
        if (!adders.length)
            return { mode: 'rest', kind: first, budget: p.fight.moveLeft };
        // A kind that `adds`: its reach on top of what is left, one reach for the turn (the server counts it the same).
        const kinds = [first, ...adders];
        const kind = kinds.find((k) => k.key === p.pick) ?? first;
        return { mode: 'kind', kinds, kind, budget: p.fight.moveLeft + (kind.adds ? tokenBudget(kind, speed, p.values, movement, p.distance) : 0) };
    }
    // A kind `alone` only in a turn with no other move; one `fightOnly` only on the token's turn in a fight.
    const noAlone = movement.owed?.noAlone === true && owesMove(p.values, movement);
    const kinds = movement.kinds.filter((k) => (!k.fightOnly || p.fight?.acting === true) && (!k.alone || (!p.fight?.moved && !noAlone)) && (!k.charge || (p.fight?.acting === true && p.fight.fresh === true)));
    const kind = kinds.find((k) => k.key === p.pick) ?? kinds[0] ?? first;
    return { mode: 'kind', kinds, kind, budget: tokenBudget(kind, speed, p.values, movement, p.distance) + (kind.adds ? tokenBudget(first, speed, p.values, movement, p.distance) : 0) };
}
//# sourceMappingURL=reach.js.map